import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { NoddiIcon } from 'noddi-ui-common';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  NoddiBasicCard,
  NoddiButton,
  NoddiInfoBanner,
  NoddiSheet,
  NoddiSkeleton,
  useIsWithinTailWindBreakpoint
} from 'noddi-ui-web';
import { type ReactNode, useState } from 'react';

export type CheckoutLoginOrder = {
  /** One line under "Your order", e.g. how many services. */
  description?: string;
  /** Formatted by the flow, which owns its decimals rule. */
  total?: string;
  /** The flow's own order summary, shown when the customer opens the row. */
  details: ReactNode;
};

type Props = {
  order: CheckoutLoginOrder;
  /** The total can still drop at login, so it is labeled as the price before login. */
  isPriceBeforeLogin?: boolean;
};

const ORDER_ITEM = 'order';

/** Holds the collapsed row's size while the order loads, so the page does not jump when it arrives. */
export const CheckoutLoginOrderRowSkeleton = () => (
  <NoddiBasicCard className='flex items-center gap-3 px-4 py-3' aria-hidden>
    <span className='flex flex-1 flex-col gap-1.5'>
      <NoddiSkeleton className='h-5 w-28' />
      <NoddiSkeleton className='h-4 w-20' />
    </span>
    <span className='flex flex-col items-end gap-1.5'>
      <NoddiSkeleton className='h-5 w-24' />
      <NoddiSkeleton className='h-3 w-16' />
    </span>
    <NoddiSkeleton className='size-4 shrink-0' />
  </NoddiBasicCard>
);

/**
 * The order, collapsed to its total so the login stays the one thing to do on this step.
 * A phone opens it in a bottom sheet; a wider screen expands it in place.
 */
export const CheckoutLoginOrderRow = ({ order, isPriceBeforeLogin }: Props) => {
  const isWithinMd = useIsWithinTailWindBreakpoint('md');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const summary = (
    <>
      <span className='flex min-w-0 flex-1 flex-col'>
        <span className='text-base font-semibold'>
          <Trans>Your order</Trans>
        </span>
        {order.description && <span className='text-sm font-normal text-foreground/70'>{order.description}</span>}
      </span>
      {order.total && (
        <span className='flex flex-col items-end'>
          <span className='text-base font-semibold whitespace-nowrap text-primary'>{order.total}</span>
          {isPriceBeforeLogin && (
            <span className='text-xs font-normal text-foreground/70'>
              <Trans>before login</Trans>
            </span>
          )}
        </span>
      )}
    </>
  );

  if (isWithinMd) {
    return (
      <>
        <NoddiBasicCard className='flex items-center gap-3 px-4 py-3' onClick={() => setIsSheetOpen(true)}>
          {summary}
          <NoddiIcon name='ChevronDown' size='small' className='shrink-0 text-primary' />
        </NoddiBasicCard>
        <NoddiSheet
          open={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          side='bottom'
          className='max-h-[90vh] rounded-t-2xl'
          closeText={t`Close`}
          title={<span className='text-lg font-semibold'>{t`Your order`}</span>}
          footer={
            <NoddiButton variant='outline' className='w-full' onClick={() => setIsSheetOpen(false)}>
              {t`Close`}
            </NoddiButton>
          }
        >
          <div className='flex flex-col gap-4'>
            {order.details}
            {isPriceBeforeLogin && (
              <NoddiInfoBanner
                variant='brand'
                title={t`Have discounts on your account? They are applied once you log in.`}
              />
            )}
          </div>
        </NoddiSheet>
      </>
    );
  }

  return (
    <NoddiBasicCard className='p-0'>
      <Accordion>
        <AccordionItem value={ORDER_ITEM} className='border-b-0'>
          <AccordionTrigger className='items-center gap-3 px-4 py-3 hover:no-underline'>{summary}</AccordionTrigger>
          <AccordionContent className='border-t px-4 pt-4'>{order.details}</AccordionContent>
        </AccordionItem>
      </Accordion>
    </NoddiBasicCard>
  );
};
